import React, { useState, useMemo, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Filter,
  User,
  Phone,
  Activity,
  Trophy,
  Search,
  Eye,
  Users,
  BarChart3,
  Target,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { scrollToTop } from "../../utils";

const ModalPortal = ({ children, isOpen }) => {
  if (!isOpen) return null;

  return ReactDOM.createPortal(children, document.body);
};

const EmployeeCard = () => {
  const [employeeData, setEmployeeData] = useState([]);

  // State management
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");

  const EmpFetch = async () => {
    try {
      const response = await axios.get(`${apiUrl}/insights/employeefetch`);
      setEmployeeData(response.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    EmpFetch();
    scrollToTop();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedEmployee) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedEmployee]);

  // Calculate total activities and redemption for each employee
  const enhancedEmployeeData = useMemo(() => {
    return employeeData.map((emp) => {
      const totalActivities = emp.activities.reduce(
        (sum, activity) => sum + activity.count,
        0
      );
      const redemptionPoints = totalActivities * 10; // Assuming 10 points per activity
      return {
        ...emp,
        totalActivities,
        redemptionPoints,
      };
    });
  }, [employeeData]);

  // Filter employees based on search
  const filteredEmployees = useMemo(() => {
    return enhancedEmployeeData.filter(
      (emp) =>
        emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.empid.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone.includes(searchTerm)
    );
  }, [enhancedEmployeeData, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmployees = filteredEmployees.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Get unique activities for filter dropdown
  const uniqueActivities = useMemo(() => {
    const activities = new Set();
    employeeData.forEach((emp) => {
      emp.activities.forEach((activity) => {
        activities.add(activity.name);
      });
    });
    return Array.from(activities);
  }, [employeeData]);

  // Filter activities in detail view
  const getFilteredActivities = (activities) => {
    let filtered = activities;

    if (dateFilter) {
      filtered = filtered.filter((activity) => activity.date === dateFilter);
    }

    if (activityFilter) {
      filtered = filtered.filter(
        (activity) => activity.name === activityFilter
      );
    }

    return filtered;
  };

  // Group activities by date
  const groupActivitiesByDate = (activities) => {
    const filtered = getFilteredActivities(activities);
    const grouped = {};

    filtered.forEach((activity) => {
      if (!grouped[activity.date]) {
        grouped[activity.date] = [];
      }
      grouped[activity.date].push(activity);
    });

    return grouped;
  };

  const EmployeeCardComponent = ({ employee }) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4">
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 p-2 rounded-full">
              <User className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-bold text-lg">{employee.name}</h3>
              <p className="text-blue-100 text-sm">{employee.empid}</p>
            </div>
          </div>
          <button
            onClick={() => setSelectedEmployee(employee)}
            className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-full transition-all duration-200"
          >
            <Eye className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center space-x-2 text-gray-600 mb-4">
          <Phone className="h-4 w-4" />
          <span className="text-sm">{employee.phone}</span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Total Activities
                </p>
                <p className="text-xl font-bold text-blue-600">
                  {employee.totalActivities}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-3 rounded-lg">
            <div className="flex items-center space-x-2">
              <Trophy className="h-5 w-5 text-green-600" />
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  Points
                </p>
                <p className="text-xl font-bold text-green-600">
                  {employee.redemptionPoints}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {employee.activities.length} unique activities recorded
          </p>
        </div>
      </div>
    </div>
  );

  const EmployeeDetailModal = ({ employee, onClose }) => (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 10000 }} // High z-index to be above navbar
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{employee.name}</h2>
              <p className="text-blue-100">
                {employee.empid} • {employee.phone}
              </p>
            </div>
            <button
              onClick={onClose}
              className="bg-white bg-opacity-20 hover:bg-opacity-30 p-2 rounded-full"
            >
              ✕
            </button>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6">
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <p className="text-blue-100 text-sm">Total Activities</p>
              <p className="text-2xl font-bold">{employee.totalActivities}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <p className="text-blue-100 text-sm">Unique Activities</p>
              <p className="text-2xl font-bold">{employee.activities.length}</p>
            </div>
            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
              <p className="text-blue-100 text-sm">Redemption Points</p>
              <p className="text-2xl font-bold">{employee.redemptionPoints}</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Date
              </label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Activity
              </label>
              <select
                value={activityFilter}
                onChange={(e) => setActivityFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Activities</option>
                {uniqueActivities.map((activity) => (
                  <option key={activity} value={activity}>
                    {activity}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setDateFilter("");
                  setActivityFilter("");
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Activities */}
        <div
          className="flex-1 p-6 overflow-y-auto"
          style={{
            maxHeight: "calc(90vh - 300px)",
            minHeight: "200px",
          }}
        >
          {employee.activities.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No activities recorded yet</p>
            </div>
          ) : (
            <div className="space-y-6 pr-2">
              {Object.entries(groupActivitiesByDate(employee.activities)).map(
                ([date, activities]) => (
                  <div key={date} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Calendar className="h-5 w-5 text-gray-600" />
                      <h3 className="font-semibold text-gray-800">
                        {new Date(date).toLocaleDateString()}
                      </h3>
                    </div>

                    <div className="grid gap-3">
                      {activities.map((activity) => (
                        <div
                          key={activity._id}
                          className="bg-white p-3 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium text-gray-800">
                                {activity.name}
                              </h4>
                              <p className="text-sm text-gray-600">
                                Count: {activity.count}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-green-600 font-medium">
                                +{activity.count * 10} points
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {getFilteredActivities(employee.activities).length === 0 &&
                (dateFilter || activityFilter) && (
                  <div className="text-center py-8 text-gray-500">
                    <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No activities found matching the current filters</p>
                  </div>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="relative mb-12 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 rounded-3xl"></div>
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-3xl"></div>

          {/* Animated Background Elements */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden rounded-3xl">
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
            <div className="absolute top-1/2 -left-8 w-16 h-16 bg-white bg-opacity-5 rounded-full animate-bounce"></div>
            <div className="absolute bottom-4 right-1/3 w-8 h-8 bg-white bg-opacity-10 rounded-full animate-ping"></div>
          </div>

          {/* Content */}
          <div className="relative z-10 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between">
              <div className="mb-6 md:mb-0">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-white bg-opacity-20 p-3 rounded-2xl backdrop-blur-sm">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                      Employee
                    </h1>
                    <p className="text-xl text-blue-100 font-light">
                      Track Employees
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-6 text-white">
                  <div className="flex items-center space-x-2 bg-white bg-opacity-10 px-4 py-2 rounded-full backdrop-blur-sm">
                    <Users className="h-5 w-5" />
                    <span className="font-medium">
                      {employeeData.length} Employees
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white bg-opacity-10 px-4 py-2 rounded-full backdrop-blur-sm">
                    <Activity className="h-5 w-5" />
                    <span className="font-medium">
                      {employeeData.reduce(
                        (sum, emp) =>
                          sum +
                          emp.activities.reduce(
                            (actSum, act) => actSum + act.count,
                            0
                          ),
                        0
                      )}{" "}
                      Total Activities
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white bg-opacity-10 px-4 py-2 rounded-full backdrop-blur-sm">
                    <Target className="h-5 w-5" />
                    <span className="font-medium">
                      {
                        employeeData.filter((emp) => emp.activities.length > 0)
                          .length
                      }{" "}
                      Active
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Stats Cards */}
              <div className="grid grid-cols-1 gap-4 w-full md:w-auto md:min-w-[280px]">
                <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-2xl p-4 border border-white border-opacity-20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">
                        Today's Activities
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {employeeData.reduce(
                          (sum, emp) =>
                            sum +
                            emp.activities
                              .filter((act) => act.date === "2025-09-26")
                              .reduce((actSum, act) => actSum + act.count, 0),
                          0
                        )}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                      <Calendar className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>

                <div className="bg-white bg-opacity-15 backdrop-blur-sm rounded-2xl p-4 border border-white border-opacity-20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">
                        Total Points Earned
                      </p>
                      <p className="text-2xl font-bold text-white">
                        {employeeData.reduce(
                          (sum, emp) =>
                            sum +
                            emp.activities.reduce(
                              (actSum, act) => actSum + act.count * 10,
                              0
                            ),
                          0
                        )}
                      </p>
                    </div>
                    <div className="bg-white bg-opacity-20 p-3 rounded-xl">
                      <Trophy className="h-6 w-6 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave Effect */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" className="w-full h-8 md:h-12">
              <path
                d="M0,120 C480,80 960,40 1440,60 L1440,120 L0,120 Z"
                fill="white"
                fillOpacity="0.1"
              />
            </svg>
          </div>
        </div>

        {/* Search and Controls */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            <div className="flex-1 max-w-md">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Search Employees
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Name, ID, or phone number..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                />
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">
                  Items per page:
                </span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                </select>
              </div>

              <div className="text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-full">
                Showing {startIndex + 1}-
                {Math.min(startIndex + itemsPerPage, filteredEmployees.length)}{" "}
                of {filteredEmployees.length}
              </div>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-8 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-blue-800 uppercase tracking-wide">
                  Total Employees
                </h3>
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-blue-700">
                {employeeData.length}
              </p>
              <p className="text-xs text-blue-600 mt-1">Registered in system</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wide">
                  Active Today
                </h3>
                <Activity className="h-6 w-6 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-green-700">
                {
                  employeeData.filter((emp) =>
                    emp.activities.some((act) => act.date === "2025-09-26")
                  ).length
                }
              </p>
              <p className="text-xs text-green-600 mt-1">Participated today</p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-purple-800 uppercase tracking-wide">
                  Total Activities
                </h3>
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-purple-700">
                {employeeData.reduce(
                  (sum, emp) =>
                    sum +
                    emp.activities.reduce(
                      (actSum, act) => actSum + act.count,
                      0
                    ),
                  0
                )}
              </p>
              <p className="text-xs text-purple-600 mt-1">Completed overall</p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-6 rounded-xl border border-amber-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wide">
                  Avg. Engagement
                </h3>
                <Target className="h-6 w-6 text-amber-600" />
              </div>
              <p className="text-3xl font-bold text-amber-700">
                {employeeData.length > 0
                  ? Math.round(
                      (employeeData.filter((emp) => emp.activities.length > 0)
                        .length /
                        employeeData.length) *
                        100
                    )
                  : 0}
                %
              </p>
              <p className="text-xs text-amber-600 mt-1">
                Employee participation
              </p>
            </div>
          </div>
        </div>

        {/* Employee Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {paginatedEmployees.map((employee) => (
            <EmployeeCardComponent key={employee._id} employee={employee} />
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center space-x-4 bg-white rounded-xl shadow-lg p-6">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Previous</span>
            </button>

            <div className="flex space-x-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                      page === currentPage
                        ? "bg-blue-600 text-white"
                        : "border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
            </div>

            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors duration-200"
            >
              <span>Next</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* No Results */}
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl shadow-lg">
            <Search className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              No employees found
            </h3>
            <p className="text-gray-600">Try adjusting your search criteria</p>
          </div>
        )}
      </div>

      {/* Modal Portal - Renders at document.body level */}
      <ModalPortal isOpen={!!selectedEmployee}>
        {selectedEmployee && (
          <EmployeeDetailModal
            employee={selectedEmployee}
            onClose={() => {
              setSelectedEmployee(null);
              setDateFilter("");
              setActivityFilter("");
            }}
          />
        )}
      </ModalPortal>
    </div>
  );
};

export default EmployeeCard;
